// "This Week" generator (handoff 7.2).
//
// Builds a Mon-Fri action list for the current week from the content log,
// the woven social schedule, and the recurring weekly rituals (newsletter
// Thursday, weekly report Friday).
//
// In-flight items surface on a stage-based weekday convention (production
// early week, publishing later — STAGE_ACTION in stages.js). Items planned
// for a future week stay off the list; overdue plan weeks stay on it.
// Published items from this week show as done so the list also reflects
// progress, not just remaining work.

import { parseLocalDate } from '../dates';
import { STAGE_ACTION, nextStage, isInFlight } from '../stages';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

export function startOfWeek(d = new Date()) {
  const dt = new Date(d);
  dt.setHours(0, 0, 0, 0);
  const diff = (dt.getDay() + 6) % 7; // ISO week start = Monday
  dt.setDate(dt.getDate() - diff);
  return dt;
}

// The Monday of the week the action list should show. The list is
// Mon-Fri, so on Saturday/Sunday it flips forward: weekend planning
// shows the coming week, not last week's finished list.
export function planningWeekStart(d = new Date()) {
  const dt = new Date(d);
  const day = dt.getDay();
  if (day === 0 || day === 6) {
    dt.setDate(dt.getDate() + (day === 6 ? 2 : 1));
  }
  return startOfWeek(dt);
}

// Local date key (toISOString would shift the Monday across midnight UTC).
function localKey(d) {
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// Keys ritual completion in localStorage — must track the SHOWN week, so
// weekend checks land on the coming week's rituals, not last week's.
export function weekKey(d = new Date()) {
  return localKey(planningWeekStart(d));
}

// Weekday index 0-4 (Mon-Fri) for a date; weekend publish dates clamp to
// Friday so nothing scheduled for Saturday silently disappears.
function weekdayIdx(date) {
  const idx = (date.getDay() + 6) % 7;
  return Math.min(idx, 4);
}

export function buildThisWeek(entries, socialPosts = [], now = new Date()) {
  const weekStart = planningWeekStart(now);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  // Raw 0-6 index (Mon=0). On weekends the shown week is the coming one,
  // so no weekday row gets the "today" mark (rawTodayIdx is 5 or 6).
  const rawTodayIdx = (new Date(now).getDay() + 6) % 7;

  const tasks = [];

  for (const e of entries || []) {
    const title = e.title || '(untitled)';
    const source = e.channel === 'youtube' ? 'youtube' : 'blog';
    const planWeek = e.planWeek ? parseLocalDate(e.planWeek) : null;
    // Work planned for a future week is next week's problem.
    const plannedAhead = planWeek && planWeek > weekEnd;

    if (e.status === 'scheduled') {
      const pub = e.publishDate ? parseLocalDate(e.publishDate) : null;
      if (pub && (pub < weekStart || pub > weekEnd)) continue;
      if (!pub && plannedAhead) continue;
      tasks.push({
        key: `content-${e.id}-publish`,
        dayIdx: pub ? weekdayIdx(pub) : STAGE_ACTION.scheduled.dayIdx,
        verb: 'Publish', title, source, contentId: e.id,
        nextStatus: nextStage('scheduled', e.channel), done: false,
      });
    } else if (isInFlight(e.status)) {
      if (plannedAhead) continue;
      const act = STAGE_ACTION[e.status];
      if (!act) continue;
      tasks.push({
        key: `content-${e.id}-${e.status}`,
        dayIdx: act.dayIdx,
        verb: act.verb, title, source, contentId: e.id,
        nextStatus: nextStage(e.status, e.channel), done: false,
      });
    } else if (e.status === 'published' && e.publishDate) {
      const pub = parseLocalDate(e.publishDate);
      if (pub >= weekStart && pub <= weekEnd) {
        tasks.push({
          key: `content-${e.id}-published`, dayIdx: weekdayIdx(pub), verb: 'Published', title,
          source, contentId: e.id, nextStatus: null, done: true,
        });
      }
    }
  }

  // Woven LinkedIn posts scheduled this week (handoff 7.1 feeds 7.2).
  // Checking one marks the social post published via the social API.
  for (const p of socialPosts || []) {
    if (!p.scheduledDate) continue;
    const d = parseLocalDate(p.scheduledDate);
    if (d < weekStart || d > weekEnd) continue;
    const done = p.status === 'published';
    const line = (p.title || (p.body || '').split('\n').find(l => l.trim()) || '(post)');
    tasks.push({
      key: `social-${p.id}`,
      dayIdx: weekdayIdx(d),
      verb: done ? 'Posted' : 'Post',
      title: line.length > 60 ? line.slice(0, 57) + '…' : line,
      source: 'linkedin',
      socialPostId: p.id,
      contentId: null,
      nextStatus: null,
      done,
    });
  }

  // Recurring rituals. Completion lives in localStorage (per-week key);
  // the report check also writes kpi_snapshots (see ThisWeekCard).
  const monthName = weekStart.toLocaleDateString('en-US', { month: 'short' });
  const weekOfMonth = Math.floor((weekStart.getDate() - 1) / 7) + 1;
  tasks.push({
    key: 'ritual-newsletter', dayIdx: 3, verb: 'Send',
    title: `newsletter · ${monthName} wk ${weekOfMonth}`,
    source: 'email', contentId: null, nextStatus: null, done: false, ritual: true,
  });
  tasks.push({
    key: 'ritual-report', dayIdx: 4, verb: 'Run',
    title: 'weekly report',
    source: 'report', contentId: null, nextStatus: null, done: false, ritual: true,
  });

  const days = DAY_LABELS.map((label, i) => {
    const date = new Date(weekStart);
    date.setDate(date.getDate() + i);
    return {
      label: `${label} · ${date.getDate()}`,
      dayIdx: i,
      isToday: i === rawTodayIdx,
      tasks: tasks.filter(t => t.dayIdx === i),
    };
  });

  return { weekStart, days, taskCount: tasks.filter(t => !t.done).length };
}
